/**
 * Better Stack RUM integration
 *
 * Loads the official Better Stack JavaScript tag when VITE_BETTERSTACK_TOKEN
 * is set (empty in local dev = no-op). The tag auto-captures page views,
 * clicks, form interactions, console logs, frontend exceptions, Web Vitals,
 * and session replays — all toggled remotely from the Better Stack UI.
 *
 * Custom business events go through `track()` (betterstack('track', ...)):
 * they appear in Events and can be used in event funnels / dashboards.
 * Reference: https://betterstack.com/docs/rum/js-tag/installation/
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

type TrackData = Record<string, string | number | boolean | null | undefined>

declare global {
  interface Window {
    betterstack?: ((cmd: string, ...args: any[]) => void) & { q?: unknown[][]; l?: number }
  }
}

/** True when the tag is configured (guards no-op dev environments). */
export const rumEnabled: boolean = Boolean(import.meta.env.VITE_BETTERSTACK_TOKEN)

/** localStorage flag that silences RUM capture entirely in this browser
 *  (owner/internal traffic). Set via console:
 *    localStorage.setItem('piccard:rum-off', '1')   // mute
 *    localStorage.removeItem('piccard:rum-off')     // unmute (debugging)
 *  Note: muted sessions also lose their own error reports — unmute when
 *  reproducing a bug you want a session replay of. */
const RUM_OFF_FLAG = 'piccard:rum-off'

/** User ids (worker users.id) whose sessions are tagged internal: traffic
 *  is still captured (errors + replays kept) but marked so Better Stack
 *  views can filter it out with a saved filter `internal != true`. */
const INTERNAL_USER_IDS: string[] = (
  (import.meta.env.VITE_INTERNAL_USER_IDS as string | undefined) ?? ''
).split(',').map((s) => s.trim()).filter(Boolean)

/** True when this browser opted out of RUM capture (owner/internal traffic). */
export function rumOptedOut(): boolean {
  try {
    return localStorage.getItem(RUM_OFF_FLAG) === '1'
  } catch {
    return false // SSR / blocked storage — default to capturing
  }
}

/** True when the signed-in user is on the internal allowlist. */
export function isInternalUser(userId: string): boolean {
  return INTERNAL_USER_IDS.includes(userId)
}

/** Inject the Better Stack tag script. Call once, as early as possible. */
export function initBetterStack(): void {
  const token = import.meta.env.VITE_BETTERSTACK_TOKEN as string | undefined
  if (!token || window.betterstack || rumOptedOut()) return

  const w = window as any
  const d = document
  const t = 'betterstack'
  w[t] =
    w[t] ||
    function (...args: unknown[]) {
      ;(w[t].q = w[t].q || []).push(args)
    }
  w[t].l = +new Date()

  const environment = detectEnvironment()
  const release = import.meta.env.VITE_APP_VERSION as string | undefined

  const s = d.createElement('script')
  s.async = true
  s.crossOrigin = 'anonymous'
  s.src = 'https://betterstack.net/b.js?t=' + token
  ;(d.head || d.getElementsByTagName('head')[0]).appendChild(s)

  w[t]('init', {
    environment,
    ...(release ? { release } : {}),
  })
}

/**
 * Send a custom event for funnels/dashboards. Silently dropped when the tag
 * is absent (local dev) or when the session is sampled out.
 */
export function track(event: string, data?: TrackData): void {
  window.betterstack?.('track', event, data)
}

/**
 * Link events/sessions/replays to a signed-in user (no PII beyond id/email).
 * NOTE: the tag command is 'user' — 'identify' (used here before 2026-08-27)
 * is not part of the JS tag API and was silently dropped, so no user ever
 * appeared on the Better Stack Users/Sessions pages. Clear with clearUser().
 * Ref: https://betterstack.com/docs/rum/identify-users/
 */
export function identifyUser(user: { id: string; email?: string; username?: string }): void {
  // Internal (owner) users carry internal: true so Better Stack views can
  // exclude them with a saved filter — errors/replays stay queryable.
  window.betterstack?.('user', { ...user, ...(isInternalUser(user.id) ? { internal: true } : {}) })
}

/** Detach the user identity at sign-out (betterstack('user', null)). */
export function clearUser(): void {
  window.betterstack?.('user', null)
}

/** Detect staging vs production from the hostname (matches deploy targets). */
function detectEnvironment(): string {
  const host = window.location.hostname
  if (host.includes('staging') || host.includes('develop') || host.endsWith('.workers.dev')) {
    return 'staging'
  }
  if (host.includes('pages.dev')) {
    return 'preview'
  }
  return 'production'
}
